import { computed, markRaw, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import { cloneCaptureProfile } from '@/core/capture-profile'
import {
  evaluateSessionStartBaseline,
  type SessionStartBaselineEvaluation,
  type SessionStartBaselineStatus,
} from '@/core/session-start-baseline'
import { SessionEngine } from '@/core/session-engine'
import { EfficiencySegmentTracker } from '@/core/efficiency-segment-tracker'
import { DEFAULT_CAPTURE_FPS, migrateRecognitionFrequencies } from '@/core/recognition-settings'
import type { CaptureProfile, GameStateSnapshot, LiveRateSnapshot, MetricKey, RankSubmissionDraft, RawReading, SessionRecord, ValidatedObservation } from '@/domain/types'
import { i18n } from '@/i18n'
import { PresetRegistry } from '@/presets/registry'
import type { GamePreset, HudLocation, PresetRecognizer } from '@/presets/types'
import { AnalysisPipeline, type PipelineResult } from '@/services/analysis-pipeline'
import { captureService, type CaptureMediaState, type CaptureStatus } from '@/services/capture-service'
import { db, RecoveryBuffer } from '@/services/database'
import {
  ensureOcrModelsCached,
  isOcrResourceLoadError,
  type OcrCachePreparationResult,
  type OcrCacheProgress,
} from '@/services/ocr-resource-cache'
import { pipService } from '@/services/pip-service'
import { enqueueRankSubmission } from '@/services/rank-api'
import { useAppStore } from './app'

export type OcrStatus = 'checking' | 'downloading' | 'initializing' | 'ready' | 'error'
export type OcrStage = 'checking' | 'detection' | 'recognition' | 'initializing' | 'ready'
export type OcrErrorType = 'resource' | 'initialization' | 'unknown'

const BACKGROUND_CONTINUITY_GAP_MS = 5_000
const SESSION_START_BASELINE_MAX_AGE_MS = 2_000
const SESSION_START_BASELINE_TIMEOUT_MS = 6_000
const SESSION_START_RETRY_DELAY_MS = 250

type SessionStartBaselineResult =
  | { readonly status: 'ready'; readonly baseline: GameStateSnapshot; readonly startedAt: number }
  | { readonly status: Exclude<SessionStartBaselineStatus, 'ready'>; readonly baseline: null }

export const useLiveStore = defineStore('live', () => {
  const app = useAppStore()
  const preset = shallowRef<GamePreset | null>(null)
  const profile = shallowRef<CaptureProfile | null>(null)
  const session = shallowRef<SessionRecord | null>(null)
  const liveRates = shallowRef<LiveRateSnapshot | null>(null)
  const gameState = shallowRef<GameStateSnapshot | null>(null)
  const rawReading = shallowRef<RawReading | null>(null)
  const hudLocation = shallowRef<HudLocation | null>(null)
  const captureStatus = ref<CaptureStatus>(captureService.status)
  const captureLabel = ref('')
  const error = ref<string | null>(null)
  const needsCalibration = ref(false)
  const ocrStatus = ref<OcrStatus>('checking')
  const ocrStage = ref<OcrStage>('checking')
  const ocrProgress = ref(0)
  const ocrCachedModels = ref(0)
  const ocrError = ref<string | null>(null)
  const ocrErrorType = ref<OcrErrorType | null>(null)
  const ocrCacheDegraded = ref(false)
  const isStarting = ref(false)
  const isEnding = ref(false)
  const trend = ref<{ at: number; exp: number | null; hp: number | null; mp: number | null; mesos: number | null }[]>([])
  const video = markRaw(captureService.video)
  const recovery = markRaw(new RecoveryBuffer())
  let pipeline: AnalysisPipeline | null = null
  let sessionEngine: SessionEngine | null = null
  let rankTracker: EfficiencySegmentTracker | null = null
  let calibrationMetric: MetricKey | null = null
  let captureGeneration = 0
  let ocrInitialization: Promise<void> | null = null
  let endSessionPromise: Promise<void> | null = null
  let startSessionPromise: Promise<void> | null = null
  let startBaselineController: AbortController | null = null
  let settlementController: AbortController | null = null
  let liveRateRefreshTimer = 0
  let lastLiveRateRefreshAt = 0
  let pageHiddenAt: number | null = null
  let pageWasFrozen = false
  let mediaContinuityLost = false
  let recoveringPipeline = false
  let readyOcrFingerprint = ''
  let cacheDegradedWarningShown = false
  let latestObservation: ValidatedObservation | null = null
  let latestObservationGeneration = -1
  let initialized = false

  const isCapturing = computed(() => captureStatus.value === 'active')
  const isRunning = computed(() => session.value?.status === 'running')
  const isPaused = computed(() => session.value?.status === 'paused')

  async function initialize(): Promise<void> {
    if (initialized) return
    initialized = true
    try {
      await loadPreset(app.presetId, false)
      captureService.addEventListener('status', ((event: CustomEvent<CaptureStatus>) => {
        captureStatus.value = event.detail
      }) as EventListener)
      captureService.addEventListener('ended', () => {
        void interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      })
      captureService.addEventListener('media-state', ((event: CustomEvent<CaptureMediaState>) => {
        handleCaptureMediaState(event.detail)
      }) as EventListener)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('freeze', handlePageFreeze)
      document.addEventListener('resume', handlePageResume)
      window.addEventListener('pageshow', handlePageResume)
    } catch (caught) {
      initialized = false
      error.value = caught instanceof Error ? `${caught.name}: ${caught.message}` : String(caught)
      ocrStatus.value = 'error'
      ocrError.value = error.value
      ocrErrorType.value = 'unknown'
      return
    }
    await initializeOcr()
  }

  async function loadPreset(id: string, initializeResources = initialized): Promise<void> {
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended' && preset.value?.id !== id) {
      throw new Error('End the active record before changing presets')
    }
    pipeline?.dispose()
    pipeline = null
    latestObservation = null
    latestObservationGeneration = -1
    stopLiveRateRefresh()
    preset.value = await PresetRegistry.load(id)
    app.presetId = id
    sessionEngine = new SessionEngine(preset.value)
    liveRates.value = null
    rankTracker = new EfficiencySegmentTracker(preset.value)
    const exactProfiles = await db.profiles.where('[presetId+presetVersion]').equals([id, preset.value.version]).toArray()
    const exact = exactProfiles.reduce<CaptureProfile | null>((latest, item) => !latest || item.updatedAt > latest.updatedAt ? item : latest, null)
    const previous = exact ? null : await db.profiles.orderBy('updatedAt').reverse().filter((item) => item.presetId === id).first()
    const migrated = previous ? preset.value.migrateProfile(previous) : null
    needsCalibration.value = Boolean(previous && !migrated)
    const selectedProfile = exact ?? migrated ?? createProfile(preset.value)
    profile.value = {
      ...selectedProfile,
      recognition: migrateRecognitionFrequencies(selectedProfile.recognition),
    }
    await db.profiles.put(profile.value)
    if (initializeResources) await initializeOcr()
  }

  async function selectWindow(): Promise<void> {
    if (isEnding.value) return
    startBaselineController?.abort()
    error.value = null
    try {
      if (session.value?.status === 'running') await interruptCapture()
      const info = await captureService.selectWindow()
      captureGeneration += 1
      mediaContinuityLost = false
      pageWasFrozen = false
      pageHiddenAt = null
      captureLabel.value = info.label
      if (!preset.value || !profile.value) return
      profile.value = { ...profile.value, sourceWidth: info.width, sourceHeight: info.height, updatedAt: Date.now() }
      await db.profiles.put(profile.value)
      startPipeline(session.value?.id ?? 'preview', null, captureGeneration)
      if (session.value?.status === 'interrupted' && sessionEngine) {
        const resumedAt = Date.now()
        sessionEngine.resume(resumedAt, captureGeneration)
        pipeline?.resetBaseline()
        session.value = sessionEngine.current(resumedAt)
        liveRates.value = sessionEngine.liveRates(resumedAt)
        startLiveRateRefresh()
        await persistSession()
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught)
      const name = typeof caught === 'object' && caught !== null && 'name' in caught ? String(caught.name) : ''
      const permissionDenied = name === 'NotAllowedError' || /permission denied|not allowed by the user/i.test(message)
      ElMessage({
        type: permissionDenied ? 'warning' : 'error',
        message: String(i18n.global.t(permissionDenied ? 'live.capturePermissionDenied' : 'live.captureFailed', { reason: message })),
        duration: 4_500,
        showClose: true,
      })
    }
  }

  function startSession(): Promise<void> {
    if (startSessionPromise) return startSessionPromise
    if (isEnding.value || (session.value && session.value.status !== 'ended')) return Promise.resolve()
    startSessionPromise = (async () => {
      isStarting.value = true
      if (!isCapturing.value) await selectWindow()
      if (!isCapturing.value || !profile.value || !sessionEngine) return

      const baselineResult = await acquireSessionStartBaseline()
      if (baselineResult.status !== 'ready') {
        ElMessage({
          type: 'warning',
          message: String(i18n.global.t(startBaselineMessageKey(baselineResult.status))),
          duration: 4_500,
          showClose: true,
        })
        return
      }

      const { baseline, startedAt } = baselineResult
      captureGeneration += 1
      session.value = sessionEngine.start(profile.value.id, startedAt, baseline, captureGeneration)
      liveRates.value = sessionEngine.liveRates(startedAt)
      startLiveRateRefresh()
      rankTracker?.reset()
      await db.sessions.put(session.value)
      trend.value = [{
        at: startedAt,
        exp: baseline.exp.ratio,
        hp: baseline.hp.ratio,
        mp: baseline.mp.ratio,
        mesos: baseline.mesos,
      }]
      startPipeline(session.value.id, baseline, captureGeneration)
    })().finally(() => {
      isStarting.value = false
      startSessionPromise = null
    })
    return startSessionPromise
  }

  async function pauseSession(): Promise<void> {
    if (!sessionEngine || isEnding.value) return
    queueCompletedSegment(rankTracker?.finish() ?? null)
    const pausedAt = Date.now()
    session.value = sessionEngine.pause(pausedAt)
    liveRates.value = sessionEngine.liveRates(pausedAt)
    stopLiveRateRefresh()
    await db.sessions.put(session.value)
  }

  async function resumeSession(): Promise<void> {
    if (!sessionEngine || isEnding.value) return
    const resumedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.resume(resumedAt, captureGeneration)
    rankTracker?.reset()
    pipeline?.setCaptureGeneration(captureGeneration)
    pipeline?.resetBaseline()
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    if (session.value) await db.sessions.put(session.value)
  }

  function endSession(): Promise<void> {
    if (endSessionPromise) return endSessionPromise
    if (!sessionEngine || !session.value || session.value.status === 'ended') return Promise.resolve()
    const engine = sessionEngine
    endSessionPromise = (async () => {
      isEnding.value = true
      if (session.value?.status === 'running' && isCapturing.value && pipeline) {
        settlementController = new AbortController()
        let timeoutId = 0
        const timeout = new Promise<boolean>((resolve) => {
          timeoutId = window.setTimeout(() => {
            settlementController?.abort()
            resolve(false)
          }, 2_000)
        })
        try {
          await Promise.race([
            pipeline.settle(video, ['level', 'exp'], settlementController.signal),
            timeout,
          ])
        } catch {
          // Keep all previously confirmed totals if the final sample fails.
        } finally {
          window.clearTimeout(timeoutId)
          settlementController.abort()
          settlementController = null
        }
      }

      const endedAt = Date.now()
      stopLiveRateRefresh()
      queueCompletedSegment(rankTracker?.finish(endedAt) ?? null)
      session.value = engine.end(endedAt)
      liveRates.value = engine.liveRates(endedAt)
      await recovery.flush(session.value, true)
    })().finally(() => {
      isEnding.value = false
      endSessionPromise = null
    })
    return endSessionPromise
  }

  async function updateProfile(next: CaptureProfile): Promise<void> {
    const sourceChanged = profile.value ? captureSourceChanged(profile.value, next) : true
    profile.value = { ...cloneCaptureProfile(next), updatedAt: Date.now() }
    needsCalibration.value = false
    pipeline?.updateProfile(profile.value)
    if (sourceChanged) {
      startBaselineController?.abort()
      captureGeneration += 1
      pipeline?.setCaptureGeneration(captureGeneration)
      pipeline?.resetBaseline()
    }
    await db.profiles.put(profile.value)
  }

  function setCalibrationMetric(metric: MetricKey | null): void {
    calibrationMetric = metric
    pipeline?.setDiagnosticMetric(metric)
  }

  async function openPip(): Promise<void> {
    await pipService.open(() => ({
      session: session.value,
      state: gameState.value,
      liveRates: liveRates.value,
      canStart: isCapturing.value && ocrStatus.value === 'ready' && !isStarting.value,
      isStarting: isStarting.value,
      isEnding: isEnding.value,
    }), {
      start: startSession,
      pause: pauseSession,
      resume: resumeSession,
      end: endSession,
      heartbeat: handlePipHeartbeat,
    }, app.locale, (level) => preset.value?.experienceToNext(level) ?? null)
  }

  async function initializeOcr(): Promise<void> {
    if (!preset.value || !profile.value) return
    if (ocrInitialization) return ocrInitialization
    const targetPreset = preset.value
    const targetProfile = profile.value
    const fingerprint = `${targetPreset.remoteResources.ocrModels.detection.url}@${targetPreset.remoteResources.ocrModels.detection.version}|${targetPreset.remoteResources.ocrModels.recognition.url}@${targetPreset.remoteResources.ocrModels.recognition.version}`
    if (ocrStatus.value === 'ready' && readyOcrFingerprint === fingerprint) return

    ocrInitialization = (async () => {
      ocrStatus.value = 'checking'
      ocrStage.value = 'checking'
      ocrProgress.value = 4
      ocrCachedModels.value = 0
      ocrError.value = null
      ocrErrorType.value = null
      ocrCacheDegraded.value = false
      const cached = new Set<string>()
      let recognizer: PresetRecognizer | null = null
      let preparation: OcrCachePreparationResult | null = null
      let startupPhase: 'resource' | 'initialization' = 'resource'
      let pendingProgress: OcrCacheProgress | null = null
      let progressFrame = 0
      const applyProgress = () => {
        progressFrame = 0
        const progress = pendingProgress
        pendingProgress = null
        if (!progress) return
        ocrStatus.value = 'downloading'
        ocrStage.value = progress.model
        ocrProgress.value = Math.max(5, Math.round(5 + progress.progress * 70))
        ocrCachedModels.value = cached.size
      }
      try {
        preparation = await ensureOcrModelsCached(targetPreset.remoteResources.ocrModels, (progress) => {
          if (progress.fromCache) cached.add(progress.model)
          pendingProgress = progress
          if (!progressFrame) progressFrame = requestAnimationFrame(applyProgress)
        })
        ocrCacheDegraded.value = preparation.cacheDegraded
        if (progressFrame) cancelAnimationFrame(progressFrame)
        applyProgress()
        startupPhase = 'initialization'
        ocrStatus.value = 'initializing'
        ocrStage.value = 'initializing'
        ocrProgress.value = 82
        recognizer = targetPreset.createRecognizer(targetProfile)
        await recognizer.warmup()
        readyOcrFingerprint = fingerprint
        ocrStage.value = 'ready'
        ocrStatus.value = 'ready'
        ocrProgress.value = 100
        if (preparation.usedTemporaryResources && !cacheDegradedWarningShown) {
          cacheDegradedWarningShown = true
          ElMessage({
            type: 'warning',
            message: String(i18n.global.t('startup.cacheDegradedWarning')),
            duration: 5_000,
            showClose: true,
          })
        }
      } catch (caught) {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        pendingProgress = null
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = isOcrResourceLoadError(caught)
          ? 'resource'
          : startupPhase === 'initialization'
            ? 'initialization'
            : 'unknown'
      } finally {
        recognizer?.dispose()
        preparation?.releaseTemporaryResources()
      }
    })().finally(() => {
      ocrInitialization = null
    })
    return ocrInitialization
  }

  async function interruptCapture(): Promise<void> {
    if (isEnding.value) {
      settlementController?.abort()
      return
    }
    startBaselineController?.abort()
    if (!sessionEngine || session.value?.status !== 'running') return
    pipeline?.stop()
    stopLiveRateRefresh()
    const interruptedAt = Date.now()
    queueCompletedSegment(rankTracker?.finish(interruptedAt) ?? null)
    session.value = sessionEngine.interrupt(interruptedAt)
    liveRates.value = sessionEngine.liveRates(interruptedAt)
    await persistSession()
  }

  async function persistSession(): Promise<void> {
    if (!session.value) return
    await db.sessions.put(session.value)
  }

  function startPipeline(sessionId: string, baseline: GameStateSnapshot | null = null, generation = captureGeneration): void {
    if (!preset.value || !profile.value) return
    pipeline?.dispose()
    let nextPipeline!: AnalysisPipeline
    nextPipeline = markRaw(new AnalysisPipeline(
      preset.value,
      profile.value,
      onPipelineResult,
      () => recoverPipeline(nextPipeline),
    ))
    pipeline = nextPipeline
    nextPipeline.setDiagnosticMetric(calibrationMetric)
    nextPipeline.start(video, sessionId, baseline, generation)
  }

  function startLiveRateRefresh(): void {
    stopLiveRateRefresh()
    lastLiveRateRefreshAt = 0
    liveRateRefreshTimer = window.setInterval(() => {
      refreshLiveRates()
    }, 1_000)
  }

  function stopLiveRateRefresh(): void {
    if (!liveRateRefreshTimer) return
    window.clearInterval(liveRateRefreshTimer)
    liveRateRefreshTimer = 0
  }

  function refreshLiveRates(now = Date.now()): void {
    if (session.value?.status !== 'running' || !sessionEngine) return
    if (now - lastLiveRateRefreshAt < 1_000) return
    lastLiveRateRefreshAt = now
    session.value = sessionEngine.current(now)
    liveRates.value = sessionEngine.liveRates(now)
  }

  function handlePipHeartbeat(): void {
    pipeline?.pulse()
    refreshLiveRates()
  }

  function recoverPipeline(expectedPipeline: AnalysisPipeline | null = pipeline): void {
    if (recoveringPipeline
      || !expectedPipeline
      || pipeline !== expectedPipeline
      || !isCapturing.value
      || !preset.value
      || !profile.value
      || isStarting.value
      || isEnding.value) return

    recoveringPipeline = true
    try {
      captureGeneration += 1
      mediaContinuityLost = false
      pageHiddenAt = null
      const sessionId = session.value?.id ?? 'preview'
      startPipeline(sessionId, gameState.value, captureGeneration)
      pipeline?.pulse(true)
    } finally {
      recoveringPipeline = false
    }
  }

  function handleCaptureMediaState(state: CaptureMediaState): void {
    if (state === 'waiting' || state === 'stalled' || state === 'paused' || state === 'muted' || state === 'idle') {
      mediaContinuityLost = true
      startBaselineController?.abort()
      return
    }
    if (state !== 'playing' && state !== 'unmuted') return

    const shouldRebaseline = mediaContinuityLost
    mediaContinuityLost = false
    if (shouldRebaseline) recoverPipeline()
    else pipeline?.pulse(true)
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      pageHiddenAt ??= Date.now()
      return
    }
    handlePageResume()
  }

  function handlePageFreeze(): void {
    pageWasFrozen = true
    startBaselineController?.abort()
    if (session.value && session.value.status !== 'ended') {
      void persistSession().catch(() => undefined)
    }
  }

  function handlePageResume(): void {
    if (!isCapturing.value || !pipeline) {
      pageWasFrozen = false
      if (document.visibilityState === 'visible') pageHiddenAt = null
      return
    }
    if (pageWasFrozen) {
      pageWasFrozen = false
      pageHiddenAt = null
      recoverPipeline()
      return
    }

    const now = Date.now()
    const health = pipeline.health()
    const missedBackgroundFrames = pageHiddenAt !== null
      && now - pageHiddenAt >= BACKGROUND_CONTINUITY_GAP_MS
      && (health?.lastSuccessfulAt === null
        || health?.lastSuccessfulAt === undefined
        || now - health.lastSuccessfulAt >= BACKGROUND_CONTINUITY_GAP_MS)
    pageHiddenAt = null
    if (missedBackgroundFrames) recoverPipeline()
    else pipeline.pulse(true)
  }

  function onPipelineResult(result: PipelineResult): void {
    if (result.captureGeneration !== captureGeneration) return
    latestObservation = result.observation
    latestObservationGeneration = result.captureGeneration
    rawReading.value = result.raw
    gameState.value = result.state
    hudLocation.value = result.hud
    const point = {
      at: result.state.capturedAt,
      exp: result.state.exp.ratio,
      hp: result.state.hp.ratio,
      mp: result.state.mp.ratio,
      mesos: result.state.mesos,
    }
    trend.value = [...trend.value.slice(-599), point]
    if (session.value?.status !== 'running' || !sessionEngine) return
    const ingested = sessionEngine.ingestWithAccounting(result.state, result.events, result.observation, result.captureGeneration)
    session.value = ingested.record
    liveRates.value = sessionEngine.liveRates(result.state.capturedAt)
    queueCompletedSegment(rankTracker?.ingest(result.state, ingested.accounting) ?? null)
    void recovery.flush(session.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  function queueCompletedSegment(submission: RankSubmissionDraft | null): void {
    if (!submission) return
    void enqueueRankSubmission(submission).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  async function acquireSessionStartBaseline(): Promise<SessionStartBaselineResult> {
    const requestedAt = Date.now()
    const existing = currentSessionStartBaseline(requestedAt, SESSION_START_BASELINE_MAX_AGE_MS)
    if (existing.status === 'ready') {
      return { status: 'ready', baseline: existing.baseline, startedAt: requestedAt }
    }
    const targetPipeline = pipeline
    const targetGeneration = captureGeneration
    if (!targetPipeline || !isCapturing.value) return { status: 'capture-unavailable', baseline: null }

    const controller = new AbortController()
    startBaselineController = controller
    let latest: SessionStartBaselineEvaluation = existing
    let completedFreshSample = false
    let timeoutId = 0
    const timeout = new Promise<null>((resolve) => {
      timeoutId = window.setTimeout(() => {
        controller.abort()
        resolve(null)
      }, SESSION_START_BASELINE_TIMEOUT_MS)
    })

    const collect = async (): Promise<SessionStartBaselineResult | null> => {
      let attempts = 0
      while (!controller.signal.aborted) {
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null }
        }

        const needsLevel = attempts === 0
          || latest.status === 'level-unconfirmed'
          || latest.status === 'table-conflict'
        const requestedMetrics: readonly MetricKey[] = needsLevel ? ['level', 'exp'] : ['exp']
        try {
          const sampled = await targetPipeline.sampleForSessionStart(video, requestedMetrics, controller.signal)
          completedFreshSample ||= sampled
        } catch {
          if (controller.signal.aborted) break
        }
        if (pipeline !== targetPipeline || captureGeneration !== targetGeneration || !isCapturing.value) {
          return { status: 'capture-unavailable', baseline: null }
        }

        latest = currentSessionStartBaseline(Date.now(), null, requestedAt)
        if (latest.status === 'ready') {
          return { status: 'ready', baseline: latest.baseline, startedAt: latest.sourceAt }
        }

        attempts += 1
        await waitForNextSessionStartFrame(controller.signal)
      }
      return null
    }

    let result: SessionStartBaselineResult | null
    try {
      result = await Promise.race([collect(), timeout])
    } finally {
      window.clearTimeout(timeoutId)
      controller.abort()
      if (startBaselineController === controller) startBaselineController = null
    }

    if (!result) {
      const captureUnavailable = pipeline !== targetPipeline
        || captureGeneration !== targetGeneration
        || !isCapturing.value
        || mediaContinuityLost
        || pageWasFrozen
      result = {
        status: captureUnavailable
          ? 'capture-unavailable'
          : !completedFreshSample || latest.status === 'capture-unavailable'
            ? 'stale'
            : latest.status,
        baseline: null,
      }
    }
    if (result.status !== 'ready'
      && pipeline === targetPipeline
      && isCapturing.value
      && !mediaContinuityLost
      && !pageWasFrozen) {
      startPipeline('preview', gameState.value, captureGeneration)
      pipeline?.pulse(true)
    }
    return result
  }

  function currentSessionStartBaseline(
    now: number,
    maximumAgeMs: number | null,
    minimumCapturedAt?: number,
  ): SessionStartBaselineEvaluation {
    if (!preset.value) {
      return { status: 'capture-unavailable' as const, baseline: null, sourceAt: null }
    }
    return evaluateSessionStartBaseline(preset.value, gameState.value, latestObservation, {
      now,
      captureGeneration,
      observationGeneration: latestObservationGeneration,
      maximumAgeMs,
      ...(minimumCapturedAt === undefined ? {} : { minimumCapturedAt }),
      mesosMaximumAgeMs: SESSION_START_BASELINE_MAX_AGE_MS,
    })
  }

  function waitForNextSessionStartFrame(signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
      let settled = false
      let callbackId: number | null = null
      const timeoutId = window.setTimeout(finish, SESSION_START_RETRY_DELAY_MS)
      const onAbort = () => finish()

      function finish(): void {
        if (settled) return
        settled = true
        window.clearTimeout(timeoutId)
        if (callbackId !== null) video.cancelVideoFrameCallback?.(callbackId)
        signal.removeEventListener('abort', onAbort)
        resolve()
      }

      if (signal.aborted) {
        finish()
        return
      }
      signal.addEventListener('abort', onAbort, { once: true })
      if (video.requestVideoFrameCallback) {
        callbackId = video.requestVideoFrameCallback(() => finish())
      }
    })
  }

  function startBaselineMessageKey(status: Exclude<SessionStartBaselineStatus, 'ready'>): string {
    if (status === 'capture-unavailable') return 'live.startBaselineCaptureUnavailable'
    if (status === 'exp-incomplete') return 'live.startBaselineIncomplete'
    if (status === 'exp-confirming' || status === 'level-unconfirmed' || status === 'table-conflict') {
      return 'live.startBaselineConfirming'
    }
    return 'live.startBaselineSlow'
  }

  function createProfile(gamePreset: GamePreset): CaptureProfile {
    const now = Date.now()
    return {
      id: crypto.randomUUID(),
      name: 'Default',
      presetId: gamePreset.id,
      presetVersion: gamePreset.version,
      sourceWidth: 1368,
      sourceHeight: 800,
      hudRect: gamePreset.hud.fallback,
      roiOverrides: {},
      colorCalibration: {},
      recognition: migrateRecognitionFrequencies({
        captureFps: DEFAULT_CAPTURE_FPS,
        minConfidence: 0.62,
        ocrChangeThreshold: 0.025,
      }),
      createdAt: now,
      updatedAt: now,
    }
  }

  function captureSourceChanged(previous: CaptureProfile, next: CaptureProfile): boolean {
    return previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.roiOverrides) !== JSON.stringify(next.roiOverrides)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
  }

  return {
    preset, profile, session, liveRates, gameState, rawReading, hudLocation, captureStatus, captureLabel, error, needsCalibration,
    ocrStatus, ocrStage, ocrProgress, ocrCachedModels, ocrError, ocrErrorType, ocrCacheDegraded, trend, video,
    isCapturing, isRunning, isPaused, isStarting, isEnding, pipSupported: pipService.supported,
    initialize, loadPreset, selectWindow, startSession, pauseSession, resumeSession, endSession, updateProfile, setCalibrationMetric, openPip, initializeOcr,
  }
})
